import {
  EmployeesEmployeeSelectorFiltersInput,
  EmployeesEmployeeSelectorInput,
  TasksValueObjectsTaskCategoryEnum,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/graphql'

import { getTaskCategoryEnumValues, getTaskKinds } from './constants'

type DateRange = {
  from: Date
  to: Date
}

type FilterValue = unknown

interface Filters {
  category?: FilterValue
  affecteeSelection?: FilterValue
  createdAt?: FilterValue
  workplace?: FilterValue
  team?: FilterValue
  legalEntity?: FilterValue
  reportingTo?: FilterValue
  timeoffManager?: FilterValue
  jobRole?: FilterValue
  accessStatus?: FilterValue
}

interface Sorting {
  field?: string
  order?: 'asc' | 'desc'
}

interface Pagination {
  cursor?: string | number | null
}

interface MapVariablesParams {
  filters: Filters
  sortings?: Sorting[]
  pagination?: Pagination
}

interface MapVariablesOptions {
  companyId: string
  completed: boolean
  manualTasksEnabled: boolean
}

/**
 * Type guard to check if a string is a valid TaskCategory
 */
export const isTaskCategory = (category?: string): category is TasksValueObjectsTaskCategoryEnum =>
  !!category && getTaskCategoryEnumValues().some((v) => v === category)

/**
 * Parse category filter values into valid TaskCategory array
 */
export const parseCategories = (
  categoryValue: FilterValue
): TasksValueObjectsTaskCategoryEnum[] => {
  const categoryValues = Array.isArray(categoryValue) ? categoryValue : []
  return categoryValues.reduce<TasksValueObjectsTaskCategoryEnum[]>((acc, c) => {
    if (c && typeof c === 'string' && isTaskCategory(c)) {
      acc.push(c)
    }
    return acc
  }, [])
}

/**
 * Parse a filter value into an array of string IDs, returns undefined if empty.
 */
const parseFilterIds = (value: FilterValue): string[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  const ids = value.filter((id): id is string | number => id != null).map((id) => String(id))
  return ids.length > 0 ? ids : undefined
}

/**
 * Parse affectee selection filter into employee selector IDs.
 */
export const parseAffecteeSelection = (
  affecteeSelectionValue: FilterValue
): { ids: string[] } | undefined => {
  const ids = parseFilterIds(affecteeSelectionValue)
  if (!ids) return undefined
  return { ids }
}

/**
 * Parse employee property filters into EmployeesEmployeeSelectorFiltersInput.
 * Returns undefined if no filters are active.
 *
 * The backend's apply_job_role_filter handles both numeric level IDs and UUID tree
 * node UUIDs automatically, so jobRole values are passed as-is regardless of the
 * job catalog feature flag.
 */
export const parseEmployeePropertyFilters = (filters: {
  workplace?: FilterValue
  team?: FilterValue
  legalEntity?: FilterValue
  reportingTo?: FilterValue
  timeoffManager?: FilterValue
  jobRole?: FilterValue
  accessStatus?: FilterValue
}): EmployeesEmployeeSelectorFiltersInput | undefined => {
  const workplace = parseFilterIds(filters.workplace)
  const team = parseFilterIds(filters.team)
  const legalEntity = parseFilterIds(filters.legalEntity)
  const reportingTo = parseFilterIds(filters.reportingTo)
  const timeoffManager = parseFilterIds(filters.timeoffManager)
  const jobRole = parseFilterIds(filters.jobRole)
  const accessStatus = parseFilterIds(filters.accessStatus)

  const hasFilters = !!(
    workplace ||
    team ||
    legalEntity ||
    reportingTo ||
    timeoffManager ||
    jobRole ||
    accessStatus
  )

  if (!hasFilters) return undefined

  return {
    ...(workplace && { workplace }),
    ...(team && { team }),
    ...(legalEntity && { legalEntity }),
    ...(reportingTo && { reportingTo }),
    ...(timeoffManager && { timeoffManager }),
    ...(jobRole && { jobRole }),
    ...(accessStatus && { accessStatus }),
  }
}

/**
 * Build the combined EmployeesEmployeeSelectorInput from affectee IDs and employee
 * property filters.
 *
 * - Only IDs selected:              { ids }                            (manual selection)
 * - Only property filters active:   { type: 'all', filters }          (filter all employees)
 * - Both active:                    { type: 'manual', ids, filters }  (AND: intersection)
 */
export const buildAffecteeSelectionInput = (
  affecteeIds: { ids: string[] } | undefined,
  propertyFilters: EmployeesEmployeeSelectorFiltersInput | undefined
): EmployeesEmployeeSelectorInput | undefined => {
  if (!affecteeIds && !propertyFilters) return undefined

  if (affecteeIds && propertyFilters) {
    return {
      type: 'manual' as const,
      ids: affecteeIds.ids,
      filters: propertyFilters,
    }
  }

  if (propertyFilters) {
    return {
      type: 'all' as const,
      filters: propertyFilters,
    }
  }

  // Only affectee IDs — preserve existing behavior
  if (affecteeIds) {
    return { ids: affecteeIds.ids }
  }

  return undefined
}

/**
 * Type guard to check if a value is a valid DateRange
 */
export const isDateRange = (value: unknown): value is DateRange =>
  !!value &&
  typeof value === 'object' &&
  'from' in value &&
  value.from instanceof Date &&
  'to' in value &&
  value.to instanceof Date

/**
 * Convert a Date to ISO string at start of day (00:00:00)
 */
export const toStartOfDayISO = (date: Date): string => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0).toISOString()
}

/**
 * Convert a Date to ISO string at end of day (23:59:59.999)
 */
export const toEndOfDayISO = (date: Date): string => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  ).toISOString()
}

/**
 * Parse createdAt date range filter into separate from/to values
 */
export const parseCreatedAtFilter = (
  createdAtValue: FilterValue
): { createdAtFrom: string; createdAtTo: string } | undefined => {
  if (!isDateRange(createdAtValue)) {
    return undefined
  }

  return {
    createdAtFrom: toStartOfDayISO(createdAtValue.from),
    createdAtTo: toEndOfDayISO(createdAtValue.to),
  }
}

/**
 * Map DataCollection filter/sorting/pagination state to GraphQL query variables
 */
export const createMapVariables = (options: MapVariablesOptions) => {
  const { companyId, completed, manualTasksEnabled } = options

  return ({ filters, sortings, pagination }: MapVariablesParams) => {
    const sortingByCreationDate = sortings?.[0]?.field === 'creationDateGroup'
    const sortingDirection = sortings?.[0]?.order

    const isFirstPage = !pagination?.cursor

    const categories = parseCategories(filters.category)

    const affecteeIds = parseAffecteeSelection(filters.affecteeSelection)
    const propertyFilters = parseEmployeePropertyFilters({
      workplace: filters.workplace,
      team: filters.team,
      legalEntity: filters.legalEntity,
      reportingTo: filters.reportingTo,
      timeoffManager: filters.timeoffManager,
      jobRole: filters.jobRole,
      accessStatus: filters.accessStatus,
    })
    const affecteeSelection = buildAffecteeSelectionInput(affecteeIds, propertyFilters)

    const createdAtFilter = parseCreatedAtFilter(filters.createdAt)

    const commonVariables = {
      affecteeSelection,
      categories,
      companyId,
      createdAtFrom: createdAtFilter?.createdAtFrom,
      createdAtTo: createdAtFilter?.createdAtTo,
      kinds: manualTasksEnabled ? undefined : getTaskKinds(),
      includeCategoryCount: isFirstPage,
      includeGroupingCount: false,
    }

    if (completed) {
      const statuses: TasksValueObjectsTaskStatusEnum[] = ['done']
      return {
        ...commonVariables,
        statuses,
        sortOrder: {
          field: 'completedAt',
          order: 'desc',
        },
      }
    }

    const statuses: TasksValueObjectsTaskStatusEnum[] = ['todo', 'in_progress']

    return {
      ...commonVariables,
      statuses,
      sortOrder: {
        field: sortingByCreationDate ? ('created_at' as const) : ('due_on' as const),
        order: sortingDirection ?? 'asc',
        nullsLast: !sortingByCreationDate,
      },
    }
  }
}
